import React, { useMemo, useCallback, useState } from "react";
import { useRouter } from "next/router";
import { observer } from "mobx-react-lite";
import { Hooks, Util } from "@xt/xtbase";
import Nav from "@xt/Nav";
import store from "store";
import TabSwitch, { TabType } from "components/pages/cfd/_cmpt/tabSwitch";
import DepositDrawer from "components/pages/cfd/_cmpt/depositDrawer";
import DepositOrConvertDrawer from "components/pages/cfd/_cmpt/depositOrConvertDrawer";
import styles from "./index.module.scss";

const { useTranslation } = Hooks;
const { getUrl } = Util;

interface Props {
  children?: React.ReactNode;
}

const TradeLayout: React.FC<Props> = ({ children }) => {
  const router = useRouter();
  const t = useTranslation();
  const { name } = store.market;
  const { isLogin } = store.user;
  const [choiceSheetOpen, setChoiceSheetOpen] = useState(false);
  const [depositDrawerOpen, setDepositDrawerOpen] = useState(false);

  // 从路由路径判断当前 tab 类型（已去掉 Spot，仅法币/U 本位）
  const activeTab = useMemo(() => {
    const path = router.pathname || router.asPath;
    if (path.includes("/trade/spot/") || path.includes("/cfd/spot/")) {
      return "uContract" as const; // 现货页会重定向到 U 本位，此处仅兜底
    }
    // 判断是 U 本位合约还是法币本位合约
    // 优先从路由中的 symbol 获取，如果没有则从 store.market.name 获取
    const { symbol } = router.query;
    const currentSymbol = (symbol as string) || name || "btc_usdt_cfd";
    const quoteCurrency = currentSymbol.split("_")[1]?.toUpperCase() || "USDT";
    const fiatCurrencies = ["AED", "BRL", "SAR"];
    const isFiat = fiatCurrencies.includes(quoteCurrency);
    
    return isFiat ? "fiatContract" as const : "uContract" as const;
  }, [router.pathname, router.asPath, router.query.symbol, name]);

  // 获取第一个可用的法币交易对
  const getFirstAvailableFiatSymbol = useCallback(() => {
    const { config } = store.market;
    if (!config) return "btc_aed_cfd"; // 默认值
    
    const fiatCurrencies = ["AED", "BRL", "SAR"];
    const baseCurrencies = ["btc", "eth", "usdt"]; // 优先尝试这些基础币种
    
    // 先尝试 btc_aed, btc_brl, btc_sar
    for (const base of baseCurrencies) {
      for (const fiat of fiatCurrencies) {
        const symbol = `${base}_${fiat.toLowerCase()}_cfd`;
        if (config[symbol] && config[symbol].state !== "DELISTED") {
          return symbol;
        }
      }
    }
    
    // 如果都没找到，返回默认值
    return "btc_aed_cfd";
  }, [store.market.config]);

  // 处理 tab 切换（已去掉 Spot）
  // 仅当当前路由为 /trade/spot/ 时才执行跳转，避免在 /cfd/contract/ 等页被误触发时把法币页改成 USDT
  const handleTabChange = useCallback(
    (tab: TabType) => {
      const path = router.pathname || router.asPath || "";
      if (!path.includes("/trade/spot/")) return;

      const { symbol } = router.query;
      const currentSymbol = (symbol as string) || "btc_usdt_cfd";
      if (tab === "uContract") {
        // 切换到U本位合约：确保是U本位交易对（非法币）
        // 如果当前是法币交易对，切换到默认的U本位交易对
        const quoteCurrency = currentSymbol.split("_")[1]?.toUpperCase();
        const fiatCurrencies = ["AED", "BRL", "SAR"];
        if (fiatCurrencies.includes(quoteCurrency)) {
          // 切换到默认的U本位交易对
          router.push(`/cfd/contract/btc_usdt_cfd`);
        } else {
          const cfdSymbol = currentSymbol.endsWith("_cfd") ? currentSymbol : `${currentSymbol}_cfd`;
          // 如果已经是正确的路由，不需要跳转
          if (router.asPath !== `/cfd/contract/${cfdSymbol}`) {
            router.push(`/cfd/contract/${cfdSymbol}`);
          }
        }
      } else if (tab === "fiatContract") {
        // 切换到法币本位合约：查找第一个可用的法币交易对
        const fiatSymbol = getFirstAvailableFiatSymbol();
        router.push(`/cfd/contract/${fiatSymbol}`);
      }
    },
    [router, getFirstAvailableFiatSymbol]
  );

  // 处理充值按钮点击：未登录跳转登录页，已登录打开底部弹窗
  const handleDepositClick = useCallback(() => {
    if (!isLogin) {
      location.href = getUrl("/accounts/login?backurl=" + encodeURIComponent(typeof location !== "undefined" ? location.href : ""));
      return;
    }
    setChoiceSheetOpen(true);
  }, [isLogin]);

  const handleChooseDeposit = useCallback(() => {
    setDepositDrawerOpen(true);
  }, []);

  return (
    <div className={styles.page}>
      <Nav mode="fixed" bgColor="#121314" bottomBar />
      <div className={styles.pageWrapper}>
        {/* Tab切换组件 */}
        <TabSwitch activeTab={activeTab} onTabChange={handleTabChange} onDepositClick={handleDepositClick} />

        {/* 子路由内容 */}
        <div className={styles.contentWrapper}>{children}</div>
      </div>

      <DepositOrConvertDrawer
        open={choiceSheetOpen}
        onClose={() => setChoiceSheetOpen(false)}
        isFiatContract={activeTab === "fiatContract"}
        onChooseDeposit={handleChooseDeposit}
      />
      <DepositDrawer 
        open={depositDrawerOpen} 
        onClose={() => setDepositDrawerOpen(false)}
      />
    </div>
  );
};

export default observer(TradeLayout);
